import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase, type Creator, type FacebookGroup } from "@/lib/supabaseClient";
import AdminCreatorCard from "@/pages/admin/components/AdminCreatorCard";
import AdminGroupCard from "@/pages/admin/components/AdminGroupCard";

const ADMIN_PASSWORD = "junkpost2024";

type Tab = "pending" | "approved" | "rejected" | "all";
type Section = "creators" | "groups";

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("jp_admin") === "true");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [section, setSection] = useState<Section>("creators");

  // Creators state
  const [creators, setCreators] = useState<Creator[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [creatorTab, setCreatorTab] = useState<Tab>("pending");
  const [creatorSearch, setCreatorSearch] = useState("");

  // Groups state
  const [groups, setGroups] = useState<FacebookGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupTab, setGroupTab] = useState<Tab>("pending");
  const [groupSearch, setGroupSearch] = useState("");

  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchCreators = async () => {
    setCreatorsLoading(true);
    const { data, error } = await supabase
      .from("creators")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setCreators(data as Creator[]);
    setCreatorsLoading(false);
  };

  const fetchGroups = async () => {
    setGroupsLoading(true);
    const { data, error } = await supabase
      .from("facebook_groups")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setGroups(data as FacebookGroup[]);
    setGroupsLoading(false);
  };

  useEffect(() => {
    if (authed) {
      fetchCreators();
      fetchGroups();
    }
  }, [authed]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (pwInput === ADMIN_PASSWORD) {
      sessionStorage.setItem("jp_admin", "true");
      setAuthed(true);
    } else {
      setPwError("Incorrect password. Please try again.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("jp_admin");
    setAuthed(false);
  };

  // Creator actions
  const handleApproveCreator = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase
      .from("creators")
      .update({ status: "approved", approved: true })
      .eq("id", id);
    if (!error) {
      setCreators((prev) => prev.map((c) => (c.id === id ? { ...c, status: "approved", approved: true } : c)));
      showToast("Creator approved and published!");
    }
    setActionLoading(false);
  };

  const handleRejectCreator = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("creators").update({ status: "rejected", approved: false }).eq("id", id);
    if (!error) {
      setCreators((prev) => prev.map((c) => (c.id === id ? { ...c, status: "rejected", approved: false } : c)));
      showToast("Creator rejected.");
    }
    setActionLoading(false);
  };

  const handleReopenCreator = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("creators").update({ status: "pending", approved: false }).eq("id", id);
    if (!error) {
      setCreators((prev) => prev.map((c) => (c.id === id ? { ...c, status: "pending", approved: false } : c)));
      showToast("Creator moved back to Pending.");
    }
    setActionLoading(false);
  };

  const handleDeleteCreator = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("creators").delete().eq("id", id);
    if (!error) {
      setCreators((prev) => prev.filter((c) => c.id !== id));
      showToast("Creator submission deleted.");
    }
    setActionLoading(false);
  };

  const handleFeatureCreator = async (id: string, featured: boolean) => {
    setActionLoading(true);
    const { error } = await supabase.from("creators").update({ featured }).eq("id", id);
    if (!error) {
      setCreators((prev) => prev.map((c) => (c.id === id ? { ...c, featured } : c)));
      showToast(featured ? "Creator marked as Featured!" : "Creator removed from Featured.");
    }
    setActionLoading(false);
  };

  // Group actions
  const handleApproveGroup = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("facebook_groups").update({ status: "approved", approved: true }).eq("id", id);
    if (!error) {
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, status: "approved", approved: true } : g)));
      showToast("Group approved and published!");
    }
    setActionLoading(false);
  };

  const handleRejectGroup = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("facebook_groups").update({ status: "rejected", approved: false }).eq("id", id);
    if (!error) {
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, status: "rejected", approved: false } : g)));
      showToast("Group rejected.");
    }
    setActionLoading(false);
  };

  const handleReopenGroup = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("facebook_groups").update({ status: "pending", approved: false }).eq("id", id);
    if (!error) {
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, status: "pending", approved: false } : g)));
      showToast("Group moved back to Pending.");
    }
    setActionLoading(false);
  };

  const handleDeleteGroup = async (id: string) => {
    setActionLoading(true);
    const { error } = await supabase.from("facebook_groups").delete().eq("id", id);
    if (!error) {
      setGroups((prev) => prev.filter((g) => g.id !== id));
      showToast("Group submission deleted.");
    }
    setActionLoading(false);
  };

  const handleFeatureGroup = async (id: string, featured: boolean) => {
    setActionLoading(true);
    const { error } = await supabase.from("facebook_groups").update({ featured }).eq("id", id);
    if (!error) {
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, featured } : g)));
      showToast(featured ? "Group marked as Featured!" : "Group removed from Featured.");
    }
    setActionLoading(false);
  };

  const creatorCounts = useMemo(() => ({
    pending: creators.filter((c) => c.status === "pending").length,
    approved: creators.filter((c) => c.status === "approved").length,
    rejected: creators.filter((c) => c.status === "rejected").length,
    all: creators.length,
  }), [creators]);

  const groupCounts = useMemo(() => ({
    pending: groups.filter((g) => g.status === "pending").length,
    approved: groups.filter((g) => g.status === "approved").length,
    rejected: groups.filter((g) => g.status === "rejected").length,
    all: groups.length,
  }), [groups]);

  const filteredCreators = useMemo(() => {
    let list = creatorTab === "all" ? creators : creators.filter((c) => c.status === creatorTab);
    if (creatorSearch) {
      const q = creatorSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.business_name.toLowerCase().includes(q) ||
          c.creator_name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          (c.specialty || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [creators, creatorTab, creatorSearch]);

  const filteredGroups = useMemo(() => {
    let list = groupTab === "all" ? groups : groups.filter((g) => g.status === groupTab);
    if (groupSearch) {
      const q = groupSearch.toLowerCase();
      list = list.filter(
        (g) =>
          g.group_name.toLowerCase().includes(q) ||
          g.admin_name.toLowerCase().includes(q) ||
          g.email.toLowerCase().includes(q) ||
          (g.description || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [groups, groupTab, groupSearch]);

  const tabs: { key: Tab; label: string }[] = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
  ];

  // --- Login Screen ---
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#3a2410] text-[#f0c87a]">
              <i className="ri-shield-keyhole-line text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-[#2c1e0f]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Access
            </h1>
            <p className="text-sm text-[#7a6555] mt-1">Junk &amp; Post Directory</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-2xl border border-[#ede5d8] p-8 flex flex-col gap-4">
            <div>
              <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                Admin Password
              </label>
              <input
                type="password"
                value={pwInput}
                onChange={(e) => { setPwInput(e.target.value); setPwError(""); }}
                placeholder="Enter password"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
              />
              {pwError && <p className="text-xs text-red-500 mt-1">{pwError}</p>}
            </div>
            <button
              type="submit"
              className="w-full py-3 rounded-full bg-[#3a2410] text-white font-semibold text-sm hover:bg-[#2c1e0f] transition-colors cursor-pointer whitespace-nowrap"
            >
              Sign In
            </button>
            <Link to="/" className="text-center text-xs text-[#9a8a78] hover:text-[#8b5e3c] transition-colors">
              ← Back to site
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-5 py-3 rounded-xl bg-[#3a2410] text-white text-sm font-medium">
          {toast}
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-[#3a2410] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-[#f0c87a]">
            <i className="ri-shield-keyhole-line"></i>
          </div>
          <div>
            <h1 className="font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-xs text-[#c9a87c]">Junk &amp; Post Directory</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-xs text-[#c9a87c] hover:text-white transition-colors whitespace-nowrap flex items-center gap-1">
            <i className="ri-external-link-line"></i> View Site
          </Link>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line mr-1"></i>Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Section Switcher */}
        <div className="flex items-center gap-2 mb-8 bg-white border border-[#ede5d8] rounded-2xl p-1.5 w-fit">
          <button
            onClick={() => setSection("creators")}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              section === "creators"
                ? "bg-[#3a2410] text-white"
                : "text-[#7a6555] hover:text-[#3a2e1e]"
            }`}
          >
            <i className="ri-user-line"></i>
            Creators
            {creatorCounts.pending > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${section === "creators" ? "bg-[#f0c87a] text-[#2c1e0f]" : "bg-amber-100 text-amber-700"}`}>
                {creatorCounts.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setSection("groups")}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              section === "groups"
                ? "bg-[#3a2410] text-white"
                : "text-[#7a6555] hover:text-[#3a2e1e]"
            }`}
          >
            <i className="ri-facebook-circle-line"></i>
            Facebook Groups
            {groupCounts.pending > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${section === "groups" ? "bg-[#f0c87a] text-[#2c1e0f]" : "bg-amber-100 text-amber-700"}`}>
                {groupCounts.pending}
              </span>
            )}
          </button>
        </div>

        {/* ---- CREATORS SECTION ---- */}
        {section === "creators" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Pending Review", value: creatorCounts.pending, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
                { label: "Approved", value: creatorCounts.approved, color: "text-green-600", bg: "bg-green-50", icon: "ri-checkbox-circle-line" },
                { label: "Rejected", value: creatorCounts.rejected, color: "text-red-500", bg: "bg-red-50", icon: "ri-close-circle-line" },
                { label: "Total Submissions", value: creatorCounts.all, color: "text-[#8b5e3c]", bg: "bg-[#f5ede0]", icon: "ri-user-line" },
              ].map((stat) => (
                <div key={stat.label} className={`${stat.bg} rounded-2xl p-4 flex items-center gap-3`}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white ${stat.color}`}>
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                    <p className="text-xs text-[#7a6555]">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-[#ede5d8] p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex items-center bg-[#f5ede0] rounded-full px-1 py-1 gap-0.5">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setCreatorTab(tab.key)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        creatorTab === tab.key ? "bg-white text-[#3a2e1e]" : "text-[#7a6555] hover:text-[#3a2e1e]"
                      }`}
                    >
                      {tab.label}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${creatorTab === tab.key ? "bg-[#f5ede0] text-[#8b5e3c]" : "bg-white/60 text-[#9a8a78]"}`}>
                        {creatorCounts[tab.key]}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="relative w-full sm:w-64">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#9a8a78]">
                    <i className="ri-search-line text-sm"></i>
                  </div>
                  <input
                    type="text"
                    value={creatorSearch}
                    onChange={(e) => setCreatorSearch(e.target.value)}
                    placeholder="Search creators..."
                    className="w-full pl-9 pr-4 py-2 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] bg-[#faf8f4]"
                  />
                </div>
              </div>
            </div>

            {creatorsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-[#ede5d8] h-32 animate-pulse" />
                ))}
              </div>
            ) : filteredCreators.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 text-[#c9a87c]">
                  <i className="ri-inbox-line text-4xl"></i>
                </div>
                <h3 className="text-lg font-bold text-[#2c1e0f] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {creatorTab === "pending" ? "No pending submissions" : "Nothing here yet"}
                </h3>
                <p className="text-sm text-[#7a6555]">
                  {creatorTab === "pending" ? "You're all caught up!" : "Try a different tab or search term."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCreators.map((creator) => (
                  <AdminCreatorCard
                    key={creator.id}
                    creator={creator}
                    onApprove={handleApproveCreator}
                    onReject={handleRejectCreator}
                    onFeature={handleFeatureCreator}
                    onDelete={handleDeleteCreator}
                    onReopen={handleReopenCreator}
                    loading={actionLoading}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ---- GROUPS SECTION ---- */}
        {section === "groups" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Pending Review", value: groupCounts.pending, color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
                { label: "Approved", value: groupCounts.approved, color: "text-green-600", bg: "bg-green-50", icon: "ri-checkbox-circle-line" },
                { label: "Rejected", value: groupCounts.rejected, color: "text-red-500", bg: "bg-red-50", icon: "ri-close-circle-line" },
                { label: "Total Groups", value: groupCounts.all, color: "text-[#8b5e3c]", bg: "bg-[#f5ede0]", icon: "ri-group-line" },
              ].map((stat) => (
                <div key={stat.label} className={`${stat.bg} rounded-2xl p-4 flex items-center gap-3`}>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white ${stat.color}`}>
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                    <p className="text-xs text-[#7a6555]">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-[#ede5d8] p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex items-center bg-[#f5ede0] rounded-full px-1 py-1 gap-0.5">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setGroupTab(tab.key)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        groupTab === tab.key ? "bg-white text-[#3a2e1e]" : "text-[#7a6555] hover:text-[#3a2e1e]"
                      }`}
                    >
                      {tab.label}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${groupTab === tab.key ? "bg-[#f5ede0] text-[#8b5e3c]" : "bg-white/60 text-[#9a8a78]"}`}>
                        {groupCounts[tab.key]}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="relative w-full sm:w-64">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#9a8a78]">
                    <i className="ri-search-line text-sm"></i>
                  </div>
                  <input
                    type="text"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="Search groups..."
                    className="w-full pl-9 pr-4 py-2 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] bg-[#faf8f4]"
                  />
                </div>
              </div>
            </div>

            {groupsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-[#ede5d8] h-32 animate-pulse" />
                ))}
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 text-[#c9a87c]">
                  <i className="ri-facebook-circle-line text-4xl"></i>
                </div>
                <h3 className="text-lg font-bold text-[#2c1e0f] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {groupTab === "pending" ? "No pending group submissions" : "No groups here yet"}
                </h3>
                <p className="text-sm text-[#7a6555]">
                  {groupTab === "pending" ? "All caught up!" : "Try a different tab or search term."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredGroups.map((group) => (
                  <AdminGroupCard
                    key={group.id}
                    group={group}
                    onApprove={handleApproveGroup}
                    onReject={handleRejectGroup}
                    onFeature={handleFeatureGroup}
                    onDelete={handleDeleteGroup}
                    onReopen={handleReopenGroup}
                    loading={actionLoading}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
